import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import Settings from "./components/settings";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") || null
  );
  const [refreshToken, setRefreshToken] = useState(
    sessionStorage.getItem("refreshToken") || null
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const axiosInstance = axios.create({
    baseURL: Settings.BASE_URL + "api/",
  });

  const setTokens = (token, refresh) => {
    setAuthToken(token);
    setRefreshToken(refresh);
    sessionStorage.setItem("accessToken", token);
    sessionStorage.setItem("refreshToken", refresh);
  };

  const setAuthTokenAndFetchProfile = async (token, refresh) => {
    setTokens(token, refresh);
    await fetchUserProfile(token);
  };

  const fetchUserProfile = async (token) => {
    try {
      const response = await axiosInstance.get("user-detail/", {
        headers: {
          Authorization: `Bearer ${token || authToken}`,
        },
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  };

  let refreshTokenPromise = null;

  const refreshAccessToken = async () => {
    if (!refreshToken || isRefreshing) return null;

    setIsRefreshing(true);

    try {
      const response = await axiosInstance.post("token/refresh/", {
        refresh: refreshToken,
      });
      const { access, refresh } = response.data;
      setTokens(access, refresh);
      return access;
    } catch (error) {
      console.error("No se puede refrescar el token de acceso:", error);
      logout();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  const logout = () => {
    setAuthToken(null);
    setRefreshToken(null);
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  };

  axiosInstance.interceptors.request.use((config) => {
    if (authToken) {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (!refreshTokenPromise) {
          refreshTokenPromise = refreshAccessToken();
        }

        try {
          const newAccessToken = await refreshTokenPromise;
          axiosInstance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (err) {
          console.error('Token not valid')
          logout();
          return Promise.reject(err);
        } finally {
          refreshTokenPromise = null;
        }
      }

      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const initializeAuth = async () => {
      if (refreshToken) {
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            await fetchUserProfile(newToken);
          }
        } catch (error) {
          console.error("Failed to refresh token on initial load:", error);
          logout();
        }
      }
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        axiosInstance,
        authToken,
        setAuthTokenAndFetchProfile,
        refreshAccessToken,
        logout,
        userProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
